import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import AddressInput from 'components/forms/inputs/rhf-inputs/AddressInput';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import PhoneNumberInput from 'components/forms/inputs/rhf-inputs/PhoneNumberInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import ReadonlyField from 'components/forms/inputs/rhf-other/ReadonlyField';
import InfoTooltip from 'components/InfoTooltip';
import { FeatureFlag } from 'types/features';
import { OfficeFormState } from './useOfficeForm';

// The office form's fields in pieces: the edit page interleaves them in its Office section as it always has, and each
// card of the office page takes the ones it edits.

/** The companies, name and initials. */
export const OfficeFields = ({ form }: { form: OfficeFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    return (
        <>
            <SelectInput
                setValue={setValue}
                control={control}
                label="offices.main-company"
                placeholder="forms.select-company"
                name="company"
                options={form.activeCompanyList}
                getOptionLabel="name"
                getOptionIdentifier="id"
                rightLabelComponent={<InfoTooltip text={'offices.tooltips.company'} />}
                required
            />
            <MultiSelectInput
                setValue={setValue}
                control={control}
                label="offices.related-companies"
                placeholder="forms.select-companies"
                name="companies"
                options={form.activeCompanyList}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
            <TextInput control={control} label="name" placeholder="forms.enter-name" name="name" required />
            <TextInput
                control={control}
                label="initials"
                placeholder="forms.enter-initials"
                name="abbreviation"
                required
                maxLength={12}
                rightLabelComponent={<InfoTooltip text={'offices.tooltips.initials'} />}
            />
        </>
    );
};

/** The e-mail address, phone number and address. */
export const OfficeContactFields = ({ form }: { form: OfficeFormState }): JSX.Element => {
    const { control } = form.methods;
    return (
        <>
            <TextInput
                control={control}
                label="email"
                placeholder="forms.enter-email"
                name="emailAddress"
                validate={form.validEmail}
            />
            <PhoneNumberInput
                control={control}
                label="phone-number"
                placeholder="forms.enter-phone-number"
                name="phoneNumber"
                maxLength={200}
            />
            <AddressInput
                label="address"
                name="address"
                control={control}
                classifiers={form.classifiers}
                required={true}
            />
        </>
    );
};

export const OfficeInvoiceAbbreviationField = ({ form }: { form: OfficeFormState }): JSX.Element => (
    <TextInput
        control={form.methods.control}
        label="offices.invoice-abbreviation"
        placeholder="forms.enter-invoice-abbreviation"
        name="invoiceAbbreviation"
        maxLength={50}
    />
);

export const OfficeLocalAddressField = ({ form }: { form: OfficeFormState }): JSX.Element => (
    <FeatureFlagged action="show" flag={FeatureFlag.LOCALIZED_FIELDS}>
        <AddressInput
            label="address-local"
            name="localAddress"
            control={form.methods.control}
            classifiers={form.classifiers}
        />
    </FeatureFlagged>
);

export const OfficeActiveField = ({ form }: { form: OfficeFormState }): JSX.Element => (
    <CheckboxInput control={form.methods.control} name="isActive" label="offices.is-active" />
);

/** The invoicing addresses and the template lists, each list's defaults shown once it has a template. */
export const OfficeInvoicingFields = ({ form }: { form: OfficeFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const { watchedInvoiceTemplates, watchedInvoiceSpecTemplates, deadlineEventSender } = form;
    return (
        <>
            <TextInput
                control={control}
                label="offices.invoicing-email"
                placeholder="forms.enter-email"
                name="invoicingEmailAddress"
                validate={form.validEmail}
            />
            {deadlineEventSender.fixedSender ? (
                <InputWrapper
                    name="deadlineEventEmailAddress"
                    label="offices.deadline-event-email"
                    hasErrors={false}
                    readonly
                >
                    <ReadonlyField value={deadlineEventSender.fixedSender} />
                </InputWrapper>
            ) : (
                <TextInput
                    control={control}
                    label="offices.deadline-event-email"
                    placeholder="forms.enter-email"
                    name="deadlineEventEmailAddress"
                    validate={form.validEmail}
                    disabled={!deadlineEventSender.isKnown}
                />
            )}
            <MultiSelectInput
                setValue={setValue}
                control={control}
                label="offices.invoice-templates"
                placeholder="offices.invoice-templates"
                name="invoiceTemplates"
                options={form.invoiceTemplates ?? []}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />

            {watchedInvoiceTemplates && watchedInvoiceTemplates.length > 0 && (
                <>
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="offices.invoice-templates-default-local"
                        placeholder="offices.invoice-templates-default-local"
                        name="defaultLocalCountryInvoiceTemplate"
                        options={watchedInvoiceTemplates ?? []}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        rightLabelComponent={<InfoTooltip text={'offices.tooltips.invoice-templates-default-local'} />}
                    />
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="offices.invoice-templates-default-foreign"
                        placeholder="offices.invoice-templates-default-foreign"
                        name="defaultForeignCountryInvoiceTemplate"
                        options={watchedInvoiceTemplates ?? []}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        rightLabelComponent={
                            <InfoTooltip text={'offices.tooltips.invoice-templates-default-foreign'} />
                        }
                    />
                </>
            )}

            <MultiSelectInput
                setValue={setValue}
                control={control}
                label="offices.spec-templates"
                placeholder="offices.spec-templates"
                name="invoiceSpecTemplates"
                options={form.invoiceSpecTemplates ?? []}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
            {watchedInvoiceSpecTemplates && watchedInvoiceSpecTemplates.length > 0 && (
                <>
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="offices.spec-templates-default-local"
                        placeholder="offices.spec-templates-default-local"
                        name="defaultLocalCountryInvoiceSpecTemplate"
                        options={watchedInvoiceSpecTemplates ?? []}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        rightLabelComponent={<InfoTooltip text={'offices.tooltips.spec-templates-default-local'} />}
                    />
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="offices.spec-templates-default-foreign"
                        placeholder="offices.spec-templates-default-foreign"
                        name="defaultForeignCountryInvoiceSpecTemplate"
                        options={watchedInvoiceSpecTemplates ?? []}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        rightLabelComponent={<InfoTooltip text={'offices.tooltips.spec-templates-default-foreign'} />}
                    />
                </>
            )}
        </>
    );
};
